/**
 * The lookups a field needs that are not on the row.
 *
 * A collaborator cell stores a user id and the name that was current when the
 * row was written; a last-modified-by cell stores the same. The authority on
 * what somebody is called now is the workspace's member list, which arrives
 * inside `GET /api/workspaces/` -- so the cells that show a person read through
 * this rather than through what the row remembers, and a rename shows in every
 * one of them at once.
 *
 * There is no member query to add here. `qk.workspaces()` already holds every
 * membership of every workspace this account is in; the dedicated endpoint is
 * admin-only and would reach a smaller audience than the rows that need it.
 */
import { useMemo } from 'react'

import type { FieldContext } from '../behavior/behavior'
import { memberLabel, sortMembers } from '@/features/workspace/members'
import { useWorkspace } from '@/features/workspace/queries'

export function useFieldContext(workspaceId: number | undefined): FieldContext {
  const { data } = useWorkspace(workspaceId)
  const users = data?.users

  return useMemo(() => {
    const collaborators = sortMembers(users ?? []).map((member) => ({
      // The account's id, not the membership's. They are different numbers and
      // the one a row stores is the account's.
      id: member.user_id,
      name: memberLabel(member),
    }))

    const names = new Map(collaborators.map(({ id, name }) => [id, name]))

    return {
      collaborators,
      // Undefined for somebody who is not a member any more, which is a real
      // answer: the row keeps the name it was written with, and that is better
      // than a blank where a person used to be.
      resolveUserName: (userId: number) => names.get(userId),
    }
  }, [users])
}
